import {
  CircledErrorIcon,
  CircledInfoIcon,
  CircledSuccessIcon,
  CircledWarningIcon,
} from "$components/icons";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import { makeStyles } from "@material-ui/core/styles";
import { CloudOff } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import classnames from "classnames";
import type { ReactNode } from "react";
import React from "react";
import DATA_TEST_IDS from "./data-testids";
import type { Variant } from "./variant";

const IconWrapper: FC<{ variant: Variant }> = ({ variant }) => {
  const classes = useStyles();

  const variantIconMap = {
    success: (
      <CircledSuccessIcon
        className={classnames(classes.icon, classes.leftIcon)}
        viewBox="0 0 26 26"
      />
    ),
    warning: (
      <CircledWarningIcon
        className={classnames(classes.icon, classes.leftIcon)}
        viewBox="0 0 26 26"
      />
    ),
    error: (
      <CircledErrorIcon
        className={classnames(classes.icon, classes.leftIcon)}
        viewBox="0 0 26 26"
      />
    ),
    info: (
      <CircledInfoIcon
        className={classnames(classes.icon, classes.leftIcon)}
        viewBox="0 0 26 26"
      />
    ),
    loading: (
      <Box className={classnames(classes.circularProgress, classes.leftIcon)}>
        <CircularProgress size={16} thickness={5} color="inherit" />
      </Box>
    ),
    offline: (
      <CloudOff
        className={classnames(classes.circularProgress, classes.leftIcon)}
      />
    ),
  };
  return variantIconMap[variant];
};

const useStyles = makeStyles((theme) => ({
  container: {
    flex: "1",
  },
  success: {
    backgroundColor: "#40bbb8",
  },
  error: {
    backgroundColor: "#de1f41",
  },
  info: {
    backgroundColor: "#3c3c3c",
  },
  warning: {
    backgroundColor: "#df8022",
  },
  loading: {
    backgroundColor: "#3c3c3c",
    flexWrap: "nowrap",
  },
  offline: {
    backgroundColor: theme.palette.grey[900],
  },
  icon: {
    opacity: 0.9,
  },
  leftIcon: {
    width: 26,
    height: 26,
    marginRight: theme.spacing(1),
  },
  circularProgress: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  message: {
    display: "flex",
    alignItems: "center",
    color: "white",
  },
}));

type IconButtonProps = React.ComponentProps<typeof IconButton>;
type SnackbarProps = Omit<
  React.ComponentProps<typeof SnackbarContent>,
  "variant"
>;

type Props = {
  message: string;
  onClose?: IconButtonProps["onClick"];
  variant?: Variant;
  actions?: Element[];
  dataTestId?: string;
} & Partial<SnackbarProps>;

export const ToastContent: FC<Props> = ({
  message,
  onClose,
  variant = "info",
  actions = [],
  dataTestId,
  ...other
}) => {
  const classes = useStyles();

  return (
    <SnackbarContent
      className={classes[variant]}
      classes={{
        message: classes.container,
      }}
      aria-describedby="client-snackbar"
      message={
        <span
          data-testid={dataTestId ?? DATA_TEST_IDS[variant]}
          id="client-snackbar"
          className={classes.message}
        >
          <Box display="flex" alignItems="center" width="100%">
            <IconWrapper variant={variant} />
            <Typography variant="subtitle2" color="inherit">
              {message}
            </Typography>
          </Box>
        </span>
      }
      action={
        [
          ...actions,
          variant === "loading" || variant === "offline" ? null : (
            <IconButton
              key="close"
              aria-label="Close"
              color="inherit"
              onClick={onClose}
            >
              <CloseIcon className={classes.icon} />
            </IconButton>
          ),
        ] as ReactNode[]
      }
      {...other}
    />
  );
};
